/**
 * AccountingTab — Project Hub accounting: payments, pay apps, statements, reports, export.
 */

import { Fragment, useState, type MouseEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchProjectPayApps, recordManualPayment, createRetainageRelease, fetchChangeOrderTrace } from '../../../api/projects'
import { payAppEditorStep } from '../../../lib/payAppEditorSteps'
import DeletePayAppDialog from './DeletePayAppDialog'
import StatementActions from '../../../components/statements/StatementActions'
import { fetchStatementRecentViews } from '../../../api/statements'
import { exportOperationsReport } from '../../../api/operationsReport'
import { fetchQBSyncErrors, loadQBStatus, syncProjectToQB } from '../../../api/quickbooks'
import { getToken } from '../../../api/client'
import type { PayApp } from '../../../api/types'
import { track } from '../../../lib/track'
import PaymentRecordInline, { EMPTY_PAYMENT_FORM, type PaymentFormState } from './PaymentRecordInline'
import PulseDownloadAll from '../../../features/pulse/PulseDownloadAll'
import { paymentsReceivedFromPayApps } from '../../../features/pulse/pulseTotals.js'
import PayAppCta from '../../../components/billing/PayAppCta'
import { isWizardEmptyDraft } from '../../../lib/payAppDrafts.js'
import { payAppCtaModel } from '../../../lib/payAppCta.js'
import ForecastExcludeRow from './ForecastExcludeRow'

function fmtMoney(raw: string | null | undefined): string {
  const n = parseFloat(raw ?? '0')
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
}

function payAppStatusChip(pa: PayApp): { label: string; cls: string } {
  if (pa.payment_status === 'paid')       return { label: `Paid · ${pa.last_payment_method ?? 'ACH'}`, cls: 'c-grn' }
  if (pa.payment_status === 'processing') return { label: 'Processing', cls: 'c-blue' }
  if (pa.status === 'draft') {
    if (isWizardEmptyDraft(pa)) return { label: 'In progress', cls: 'c-blue' }
    return { label: 'Draft', cls: 'c-blue' }
  }
  if (pa.submitted_at) {
    const age = (Date.now() - new Date(pa.submitted_at).getTime()) / 86_400_000
    if (age > 30) return { label: `${Math.floor(age - 30)}d late`, cls: 'c-red' }
  }
  return { label: 'Submitted', cls: 'c-amb' }
}

interface Props {
  projectId: number
  ownerName?: string | null
  ownerEmail?: string | null
  excludeFromForecast?: boolean
}

export default function AccountingTab({ projectId, ownerName, ownerEmail, excludeFromForecast = false }: Props) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [recordingPayAppId, setRecordingPayAppId] = useState<number | null>(null)
  const [paymentInitial, setPaymentInitial] = useState<PaymentFormState>(EMPTY_PAYMENT_FORM)

  const { data: payApps = [], isPending } = useQuery({
    queryKey:  ['project-payapps', projectId],
    queryFn:   () => fetchProjectPayApps(projectId),
    staleTime: 30_000,
  })

  const { data: qbStatus } = useQuery({
    queryKey: ['qb-status'],
    queryFn: loadQBStatus,
    staleTime: 60_000,
  })

  const { data: qbSyncErrors = [] } = useQuery({
    queryKey: ['qb-sync-errors'],
    queryFn: fetchQBSyncErrors,
    enabled: !!qbStatus?.connected,
    staleTime: 15_000,
  })

  const qbErrorByPayApp = new Map<number, string>()
  for (const e of qbSyncErrors) {
    if (e.local_entity_type === 'pay_app') {
      qbErrorByPayApp.set(e.local_entity_id, e.last_error || 'Sync failed')
    }
  }

  const [qbSyncBusy, setQbSyncBusy] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<PayApp | null>(null)
  const [retentionBusy, setRetentionBusy] = useState(false)

  const { data: projectMeta } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const { fetchProject } = await import('../../../api/projects')
      return fetchProject(projectId)
    },
    staleTime: 30_000,
  })

  const { data: coTrace = [] } = useQuery({
    queryKey: ['co-trace', projectId],
    queryFn: () => fetchChangeOrderTrace(projectId),
    staleTime: 30_000,
  })

  const { data: statementViews = [] } = useQuery({
    queryKey: ['statement-recent-views', projectId],
    queryFn: () => fetchStatementRecentViews(projectId),
    staleTime: 30_000,
    enabled: !!ownerName,
  })

  const [reportExportBusy, setReportExportBusy] = useState(false)
  const [csvExportBusy, setCsvExportBusy] = useState(false)

  const retentionHeld = parseFloat(projectMeta?.retention_held ?? '0') || 0
  const hasRetainageRelease = payApps.some(pa => pa.is_retainage_release)
  const ctaModel = payAppCtaModel({
    project: projectMeta ?? null,
    payApps,
    retentionHeld,
    hasRetainageRelease,
  })
  const unbilledCos = coTrace.filter(c => c.billing_status === 'not_yet_billed')

  async function handleBillRetention(e: MouseEvent) {
    e.stopPropagation()
    setRetentionBusy(true)
    try {
      const result = await createRetainageRelease(projectId)
      await qc.invalidateQueries({ queryKey: ['project-payapps', projectId] })
      if (result.pay_app_id) {
        navigate(`/projects/${projectId}/pay-apps/${result.pay_app_id}?step=1`)
      }
    } finally {
      setRetentionBusy(false)
    }
  }

  async function handleQbProjectSync(e: MouseEvent) {
    e.stopPropagation()
    setQbSyncBusy(true)
    try {
      await syncProjectToQB(projectId)
      await qc.invalidateQueries({ queryKey: ['qb-sync-errors'] })
    } finally {
      setQbSyncBusy(false)
    }
  }

  const { apps: paidApps, collected: ytd } = paymentsReceivedFromPayApps(payApps)

  function openEditor(payAppId: number | null, pa?: PayApp) {
    if (payAppId == null) {
      navigate(`/projects/${projectId}/pay-apps/new`)
      return
    }
    const step = pa ? payAppEditorStep(pa) : 1
    navigate(`/projects/${projectId}/pay-apps/${payAppId}?step=${step}`)
  }

  function openPaymentForm(pa: PayApp, e: MouseEvent) {
    e.stopPropagation()
    const raw = pa.payment_status === 'paid'
      ? parseFloat(pa.amount_paid ?? pa.last_payment_amount ?? '0')
      : parseFloat(pa.amount_due ?? '0')
    setRecordingPayAppId(pa.id)
    setPaymentInitial({
      amount: raw > 0
        ? raw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '',
      method: pa.last_payment_method ?? 'Check',
      checkNumber: pa.last_check_number ?? '',
      notes: '',
    })
  }

  function closePaymentForm(e?: MouseEvent) {
    e?.stopPropagation()
    setRecordingPayAppId(null)
    setPaymentInitial(EMPTY_PAYMENT_FORM)
  }

function showPaymentButton(pa: PayApp): 'record' | 'edit' | null {
  if (pa.payment_status === 'paid') return 'edit'
  const isSubmitted = pa.status === 'submitted' || !!pa.submitted_at
  if (isSubmitted && pa.payment_status !== 'processing') return 'record'
  return null
}

function canDeletePayApp(pa: PayApp): boolean {
  return pa.payment_status !== 'paid' && pa.status !== 'paid'
}

  return (
    <div>
      <div className="pm-section-h">
        <div>
          <div className="pm-section-tt">Payments received</div>
          <div className="pm-section-st">What actually hit your bank</div>
        </div>
        {ytd > 0 && (
          <div style={{ fontSize: 11, color: 'var(--ink4)' }}>
            YTD:{' '}
            <b style={{ color: 'var(--green)', fontFamily: "'JetBrains Mono',monospace" }}>
              {fmtMoney(String(ytd))}
            </b>
          </div>
        )}
      </div>

      <div className="panel" style={{ marginBottom: 18 }}>
        {isPending ? (
          <div style={{ padding: '16px', fontSize: 12, color: 'var(--ink4)' }}>Loading…</div>
        ) : paidApps.length === 0 ? (
          <div style={{ padding: '16px', fontSize: 12, color: 'var(--ink4)', textAlign: 'center' }}>
            No payments recorded yet.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Date</th><th>Method</th><th>For</th>
                <th className="r">Gross</th><th className="r">Net to you</th>
              </tr>
            </thead>
            <tbody>
              {paidApps.map(pa => (
                <tr key={pa.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{fmtDate(pa.last_payment_date ?? pa.submitted_at)}</td>
                  <td>{pa.last_payment_method ?? 'ACH'}</td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {pa.app_number != null ? `PA #${pa.app_number}` : 'Draft'}
                  </td>
                  <td className="r mono-c">{fmtMoney(pa.amount_due)}</td>
                  <td className="r mono-c" style={{ color: 'var(--green)' }}>{fmtMoney(pa.amount_paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pm-section-h" style={{ marginTop: 18 }}>
        <div>
          <div className="pm-section-tt">Pay applications sent</div>
          <div className="pm-section-st">Standard pay application · invoiced to owner</div>
        </div>
        <PayAppCta
          projectId={projectId}
          project={projectMeta ?? null}
          payApps={payApps}
          testId="accounting-pay-app-cta"
        />
        {retentionHeld > 0 && !hasRetainageRelease && ctaModel.variant !== 'retention' && (
          <button
            type="button"
            className="btn b-grn btn-sm"
            disabled={retentionBusy}
            data-testid="payments-bill-retention-btn"
            onClick={handleBillRetention}
          >
            {retentionBusy ? 'Creating…' : `Bill retention (${fmtMoney(String(retentionHeld))})`}
          </button>
        )}
        {qbStatus?.connected && !qbStatus.needs_reconnect && (
          <button
            type="button"
            className="btn b-ol btn-sm"
            disabled={qbSyncBusy}
            onClick={handleQbProjectSync}
            data-testid="payments-qb-sync-btn"
          >
            {qbSyncBusy ? 'Syncing…' : 'Sync to QuickBooks'}
          </button>
        )}
      </div>

      <div className="panel" style={{ marginBottom: 14 }}>
        {isPending ? (
          <div style={{ padding: '16px', fontSize: 12, color: 'var(--ink4)' }}>Loading…</div>
        ) : payApps.length === 0 ? (
          <div style={{ padding: '16px', fontSize: 12, color: 'var(--ink4)', textAlign: 'center' }}>
            No pay applications yet.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th><th>Sent</th><th>Period</th>
                <th className="r">Amount</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payApps.map(pa => {
                const { label, cls } = payAppStatusChip(pa)
                const isRecording = recordingPayAppId === pa.id
                const paymentAction = showPaymentButton(pa)
                const canEdit = paymentAction === 'edit'
                const isDraft = pa.status === 'draft'
                return (
                  <Fragment key={pa.id}>
                    <tr
                      onClick={() => openEditor(pa.id, pa)}
                      style={{ cursor: 'pointer' }}
                      data-testid={`payapp-row-${pa.app_number ?? pa.id}`}
                      data-focus-id={`pay_app:${pa.id}`}
                    >
                      <td className="mono" style={{ fontSize: 11 }}>
                    {pa.app_number != null ? `PA #${pa.app_number}` : 'Draft'}
                  </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {pa.submitted_at
                          ? fmtDate(pa.submitted_at)
                          : pa.status === 'submitted' || pa.status === 'paid'
                            ? '—'
                            : isWizardEmptyDraft(pa)
                              ? 'In progress'
                              : 'Draft'}
                      </td>
                      <td>{pa.period_label?.trim() ? pa.period_label : '—'}</td>
                      <td className="r mono-c">
                        {isWizardEmptyDraft(pa) ? '—' : fmtMoney(pa.amount_due)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                          <span className={`chip ${cls}`}>{label}</span>
                          {qbErrorByPayApp.has(pa.id) && (
                            <span
                              className="chip c-red"
                              title={qbErrorByPayApp.get(pa.id)}
                              data-testid={`qb-sync-error-${pa.app_number}`}
                            >
                              QB sync failed · Retry
                            </span>
                          )}
                          {isDraft && (
                            <button
                              type="button"
                              className="btn b-ol btn-sm"
                              onClick={e => {
                                e.stopPropagation()
                                openEditor(pa.id, pa)
                              }}
                              data-testid={`payapp-edit-${pa.app_number}`}
                            >
                              Edit
                            </button>
                          )}
                          {canDeletePayApp(pa) && (
                            <button
                              type="button"
                              className="btn b-ol btn-sm"
                              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                              onClick={e => {
                                e.stopPropagation()
                                setDeleteTarget(pa)
                              }}
                              data-testid={`payapp-delete-${pa.app_number}`}
                            >
                              Delete
                            </button>
                          )}
                          {paymentAction && (
                            <button
                              type="button"
                              className="btn b-ol btn-sm"
                              onClick={e => openPaymentForm(pa, e)}
                              data-testid={`payapp-record-${pa.app_number}`}
                            >
                              {canEdit ? 'Edit Payment' : 'Record Payment'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                                        {isRecording && (
                      <tr onClick={e => e.stopPropagation()}>
                        <td colSpan={5} style={{ padding: '12px 14px', background: 'var(--bg2)' }}>
                          <PaymentRecordInline
                            key={pa.id}
                            appNumber={pa.app_number}
                            canEdit={canEdit}
                            initial={paymentInitial}
                            alreadyPaid={pa.payment_status === 'paid'}
                            onCancel={closePaymentForm}
                            onSubmit={async (payload) => {
                              await recordManualPayment(projectId, pa.id, payload)
                              track('payment_recorded', { project_id: projectId, pay_app_id: pa.id })
                              await qc.invalidateQueries({ queryKey: ['project-payapps', projectId] })
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {unbilledCos.length > 0 && (
        <div className="panel" style={{ marginBottom: 14, padding: 14 }} data-testid="co-trace-panel">
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 10 }}>
            Change orders — billing status
          </div>
          {unbilledCos.map(co => (
            <div
              key={co.id}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: '1px solid var(--line)', fontSize: 12,
              }}
            >
              <span>
                <b>CO-{co.co_number}</b> {co.description}
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="mono-c">{fmtMoney(String(co.amount))}</span>
                <span className="chip c-amb">Not yet billed</span>
                <button
                  type="button"
                  className="btn b-ol btn-sm"
                  onClick={() => openEditor(co.pay_app_id)}
                >
                  Open PA #{co.app_number}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {ownerName && (
        <div style={{ marginTop: 24, marginBottom: 18 }} data-testid="accounting-statements-section">
          <div className="pm-section-h">
            <div>
              <div className="pm-section-tt">Statements</div>
              <div className="pm-section-st">Owner statement — view, PDF, email, or share link</div>
            </div>
          </div>
          <div className="panel" style={{ padding: 14 }}>
            <StatementActions
              direction="owner"
              label="Owner statement"
              variant="primary"
              testId="accounting-owner-statement"
              ownerEmail={ownerEmail || undefined}
              params={{ owner_name: ownerName, project_ids: [projectId], project_id: projectId }}
            />
            {statementViews.length > 0 && (
              <div style={{ marginTop: 14, fontSize: 12, color: 'var(--ink3)' }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--ink)' }}>View log</div>
                {statementViews.slice(0, 5).map((v, i) => (
                  <div key={`${v.viewed_at}-${i}`} style={{ marginBottom: 4 }}>
                    Owner viewed {new Date(v.viewed_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 24, marginBottom: 18 }} data-testid="accounting-reports-section">
        <div className="pm-section-h">
          <div>
            <div className="pm-section-tt">Reports</div>
            <div className="pm-section-st">Operations report scoped to this project</div>
          </div>
        </div>
        <div className="panel" style={{ padding: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link
            to={`/reports/owner-statement?owner=${encodeURIComponent(ownerName || '')}`}
            className="btn b-ol btn-sm"
            data-testid="accounting-statement-link"
          >
            Statement
          </Link>
          <Link
            to={`/reports/operations?project_id=${projectId}`}
            className="btn b-ol btn-sm"
            data-testid="accounting-operations-link"
          >
            View operations report
          </Link>
          <button
            type="button"
            className="btn b-ol btn-sm"
            disabled={reportExportBusy}
            data-testid="accounting-operations-pdf"
            onClick={async () => {
              setReportExportBusy(true)
              try {
                const { blob, filename } = await exportOperationsReport('pdf', { projectId: String(projectId) })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = filename
                a.click()
                URL.revokeObjectURL(url)
              } finally {
                setReportExportBusy(false)
              }
            }}
          >
            {reportExportBusy ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 18 }} data-testid="accounting-export-section">
        <div className="pm-section-h">
          <div>
            <div className="pm-section-tt">Export</div>
            <div className="pm-section-st">Download pay applications for this project as CSV</div>
          </div>
        </div>
        <div className="panel" style={{ padding: 14 }}>
          <button
            type="button"
            className="btn b-ol btn-sm"
            disabled={csvExportBusy}
            data-testid="accounting-project-csv"
            onClick={async () => {
              setCsvExportBusy(true)
              try {
                const token = getToken()
                const res = await fetch(`/api/reports/export?project_id=${projectId}`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                })
                if (!res.ok) throw new Error('Export failed')
                const blob = await res.blob()
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `project-${projectId}-pay-apps.csv`
                a.click()
                URL.revokeObjectURL(url)
              } finally {
                setCsvExportBusy(false)
              }
            }}
          >
            {csvExportBusy ? 'Exporting…' : 'Download project CSV'}
          </button>
          <span style={{ marginLeft: 8 }}>
            <PulseDownloadAll projectId={projectId} testId="accounting-download-all" />
          </span>
        </div>
      </div>

      <div style={{
        background: 'var(--amber-l)', border: '1px solid var(--amber)',
        borderRadius: 'var(--rs)', padding: '11px 14px',
        fontSize: 11, color: 'var(--ink3)', lineHeight: 1.55,
      }}>
        <strong style={{ color: 'var(--ink)' }}>Stripe Connect:</strong>{' '}
        Accept card and ACH payments directly · funds in 2 business days.{' '}
        <button
          style={{
            background: 'none', border: 'none', padding: 0,
            color: 'var(--ink)', fontWeight: 600, fontSize: 11,
            cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline',
          }}
          onClick={() => navigate('/settings')}
          data-testid="payments-settings-link"
        >
          Set up in Settings →
        </button>
      </div>

      <ForecastExcludeRow projectId={projectId} initial={excludeFromForecast} />

      {deleteTarget && (
        <DeletePayAppDialog
          payApp={deleteTarget}
          open
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null)
            void qc.invalidateQueries({ queryKey: ['project-payapps', projectId] })
          }}
        />
      )}
    </div>
  )
}
